import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/authStore';
import { loginWithEmail, getIdToken, onAuthStateChange, getUserRole, logout } from '@/shared/services/authService';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const navigate = useNavigate();
  const { setAuth, isAuthenticated, clearAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Verificar si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Observar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Obtener el token de ID (forzar refresh para obtener custom claims actualizados)
          const token = await getIdToken(true);
          
          if (!token) {
            return;
          }

          // Obtener el rol del usuario desde los custom claims
          const userRole = await getUserRole();

          // Verificar que el usuario sea admin
          if (userRole !== 'admin') {
            // Cerrar sesión si no es admin
            await logout();
            clearAuth();
            return;
          }

          // Si es admin, establecer autenticación
          setAuth(firebaseUser, 'admin');
          
          // Redirigir al dashboard solo si estamos en login
          if (window.location.pathname === '/login') {
            navigate('/dashboard');
          }
        } catch (err) {
          console.error('Error al obtener token o rol:', err);
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, setAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email || !password) {
        setError('Por favor completa todos los campos');
        setLoading(false);
        return;
      }

      // Iniciar sesión con Firebase
      const userCredential = await loginWithEmail(email, password);
      const firebaseUser = userCredential.user;

      // Obtener token de ID (forzar refresh para obtener custom claims actualizados)
      const token = await getIdToken(true);
      
      if (!token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      // Obtener el rol del usuario desde los custom claims
      const userRole = await getUserRole();

      // Verificar que el usuario sea admin
      if (userRole !== 'admin') {
        // Cerrar sesión si no es admin
        await logout();
        setError('Acceso denegado. Solo los administradores pueden acceder a este panel.');
        setLoading(false);
        return;
      }

      // Si es admin, establecer autenticación y redirigir
      setAuth(firebaseUser, 'admin');
      
      // Redirigir al dashboard
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión. Verifica tus credenciales.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>EncoreEats</h1>
          <p className={styles.subtitle}>Panel de Administración</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.field}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@encoreeats.com"
              required
              autoComplete="email"
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>

        </form>

        <div className={styles.footer}>
          <p>
            ¿No tienes cuenta?{' '}
            <button
              type="button"
              className={styles.linkButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Navegando a /register');
                navigate('/register');
              }}
            >
              Regístrate aquí
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

